import { AfterViewInit, ChangeDetectionStrategy, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { Subject, Subscription, timer } from 'rxjs';
import { filter, retry, takeUntil, timeout } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { FormControlEx } from '../../../core/component/forms/form-control';
import { AstralTab } from '../../../core/component/astral-components/astral-tabs/astral-tabs.component';
import { IsMobileDevice } from '../../../core/helper/mobile';
import { MessageService } from '../../../core/service/message.service';
import { FormControlExFactory } from '../../../core/service/factory/form-control-ex-factory.service';
import { AccountService } from '../../service/data/account.service';
import { UserAccountService } from '../../service/user-account.service';

@Component({
    selector: 'app-login',
    templateUrl: './login.component.html',
    styleUrls: ['./login.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class LoginComponent implements OnInit, OnDestroy, AfterViewInit {
  constructor(
    private readonly _account: AccountService,
    private readonly _userAccount: UserAccountService,

    private readonly _fb: FormBuilder,
    private readonly _cb: FormControlExFactory,
    private readonly _messageService: MessageService
  ) {
    this.loginForm = this._fb.group({
      login: this._cb.createMandatory('LOGIN_FORM.LOGIN_LABEL', null),
      password: this._cb.createMandatory('LOGIN_FORM.PASSWORD_LABEL', null),
      remember: this._cb.create('LOGIN_FORM.REMEMBER_ME_LABEL', true),
    });

    this.restoreForm = this._fb.group({
      restore: this._cb.createEmailMandatory('RESTORE_PASSWORD_FORM.EMAIL_LABEL', ''),
    });

    this.envName = environment.name;
  }
  protected readonly envName?: string;

  @ViewChild('bankIdContainer') containerElement!: ElementRef;

  authMethod: 'bankid' | 'password' = 'password';
  readonly authMethods: readonly AstralTab[] = [
    { id: 'password', labelKey: 'LOGIN_FORM.CREDENTIALS' },
    { id: 'bankid', labelKey: 'LOGIN_FORM.BANKID' },
  ];

  ngAfterViewInit(): void {
    document.addEventListener('fullscreenchange', this.onFullscreenChange);
  }

  onFullscreenChange = () => {
    const el = this.containerElement?.nativeElement;
    if (!el) return;

    if (document.fullscreenElement === el) {
      el.classList.add('fullscreen-active');
    } else {
      el.classList.remove('fullscreen-active');
    }
  };

  // Typed accessors so the built-in [formControl] directive (expects FormControl)
  // accepts these controls under strictTemplates.
  get loginControl(): FormControl {
    return this.loginForm.controls['login'] as FormControl;
  }

  get passwordControl(): FormControl {
    return this.loginForm.controls['password'] as FormControl;
  }

  get restoreControl(): FormControlEx {
    return this.restoreForm.controls['restore'] as FormControlEx;
  }

  get restoreEmail(): string {
    return (this.restorePassword && this.restoreControl.value) || '';
  }

  private _subscription = new Subscription();
  private _loginSubscription = new Subscription();

  loginForm: FormGroup;
  errorMessage: string;
  isEmailSent: boolean = false;
  restoreForm: FormGroup;
  restorePassword: boolean = false;
  isMobile: boolean;

  //BankId related
  protected running: boolean = false;
  protected promptBankId: boolean = true;
  protected showBankIdLoginWindow: boolean = false;
  protected isAutoStart: boolean = false;
  protected showCanceledDialog: boolean = false;
  protected showFailedStatusWindow: boolean = false;

  goBack() {
    this.restorePassword = false;
    this.isEmailSent = false;
    this.restoreForm.reset();
  }

  goRestore() {
    if (this.loginForm.disabled) return;

    this.restorePassword = true;
  }

  selectAuthMethod(method: 'bankid' | 'password') {
    if (!this.loginForm.disabled) {
      this.authMethod = method;
    }
  }

  login(): void {
    if (this.loginForm.invalid) {
      this.loginForm.markAllAsTouched();
      return;
    }

    this.loginForm.disable();
    let jsonObject = JSON.parse(JSON.stringify(this.loginForm.value));
    this._subscription.add(
      this._userAccount.login(jsonObject.login, jsonObject.password, jsonObject.remember).subscribe({
        error: error => {
          this.loginForm.enable();
          if (error instanceof Error && error.message) {
            this.loginForm.controls['login'].setErrors({ user: { message: error.message } });
            this.loginForm.controls['password'].setErrors({ password: { message: error.message } });
          }
        },
      })
    );
  }

  ngOnInit(): void {
    this.isMobile = IsMobileDevice();
  }

  ngOnDestroy() {
    this._subscription.unsubscribe();
    this._loginSubscription?.unsubscribe();
    document.removeEventListener('fullscreenchange', this.onFullscreenChange);
  }

  restore() {
    if (this.restoreForm.invalid) {
      const remainingErrors = this.restoreControl.removeError('oops');
      if (remainingErrors) {
        return;
      }
    }
    this._account.forgotPassword(this.restoreControl.value).subscribe({
      next: () => (this.isEmailSent = true),
      error: () => {
        this.restoreControl.setErrors({ oops: { message: 'RESTORE_PASSWORD_FORM.SOMETHING_WENT_WRONG_SHORT' } });
        this._messageService.showError('RESTORE_PASSWORD_FORM.SOMETHING_WENT_WRONG');
      },
    });
  }

  startBankId(isAutoStart: boolean) {
    if (this.running) {
      return;
    }
    this.running = true;
    this.isAutoStart = isAutoStart;
  }

  stopRunning() {
    if (document.fullscreenElement) {
      document.exitFullscreen().then(() => this.containerElement.nativeElement.classList.remove('fullscreen-active'));
    }
    this.running = false;
  }

  onResult(orderReference: string) {
    if (orderReference) {
      this._loginSubscription = this._userAccount.loginBankId(orderReference).subscribe();
    } else {
      this.running = false;
      return;
    }
  }

  toggleBankIdLoginWindow() {
    if (this.running) {
      this.running = false;
    }
    this.showBankIdLoginWindow = !this.showBankIdLoginWindow;
  }

  onRequestFullscreen() {
    const el = this.containerElement?.nativeElement;
    if (!el) return;

    if (!document.fullscreenElement) {
      el.requestFullscreen();
    } else {
      document.exitFullscreen();
    }
  }
}
